import { Footer } from "@components/Footer/Footer";
import { JWMHeaderHero } from "@components/JWMHeaderHero/JWMHeaderHero";
import { JWMHtml } from "@components/JWMHtml/JWMHtml";
import { JWMLoginDisclaimer } from "@components/JWMLoginDisclaimer/JWMLoginDisclaimer";
import { PlatformLogo } from "@components/PlatformLogo/PlatformLogo";
import RestrictedPageContent from "@components/RestrictedPageContent/RestrictedPageContent";
import useMainNavigation from "@hooks/useMainNavigation/useMainNavigation";
import useUserInfo from "@hooks/useUserInfo/useUserInfo";
import analyticstracker from "@jmc/analyticstracker";
import { Header } from "@jmc/core/src/components/Header/Header";
import { NavigationImpression } from "@jmc/core/src/components/NavigationImpression/NavigationImpression";
import { ToolTracking } from "@jmc/core/src/components/Tracking";
import { EventTypes } from "@jmc/core/src/types/EventTypes";
import { Button } from "@jmc/solid-design-system/src/components/atoms/Button/Button";
import { CheckBox } from "@jmc/solid-design-system/src/components/atoms/CheckBox/CheckBox";
import { Spinner } from "@jmc/solid-design-system/src/components/atoms/Spinner/Spinner";
import { Typography } from "@jmc/solid-design-system/src/components/atoms/Typography/Typography";
import { useJnjBranding } from "@jmc/utils/hooks/useJnjBranding";
import { CMSMinisite } from "@jwm/types/CMSMinisite";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { AccessLevel } from "../../types/AccessLevel";
import style from "./style.module.scss";

export interface PropTypes {
    children: JSX.Element | JSX.Element[];
    siteReference: CMSMinisite;
    access_level?: string;
}

export const PatientDeclaration = (props: PropTypes): JSX.Element => {
    const { children, siteReference, access_level = AccessLevel.Public } = props;
    const isBrowser = typeof document !== "undefined";
    const declarationUid = siteReference?.patient_declaration?.[0]?.uid;
    const store = isBrowser ? JSON.parse(sessionStorage.getItem(`patientDeclaration`) || "null") || [] : [];
    const accepted = store?.indexOf(declarationUid) > -1;
    const declarationTitle = siteReference?.patient_declaration?.[0]?.title;
    const declaration = siteReference?.patient_declaration?.[0]?.declaration;
    const legalDisclaimer = siteReference?.patient_declaration?.[0]?.legal_disclaimer;
    const sideEffects = siteReference?.patient_declaration?.[0]?.side_effects;
    const footer = siteReference?.branding?.footer?.[0];
    const { jnjFullBranded } = useJnjBranding();
    let imrNumber = footer?.imr_number;
    let footerLastUpdated = footer?.updated_at;
    const configuration = useMainNavigation();
    const userInfo = useUserInfo();

    const { t } = useTranslation();

    if (siteReference?.imr_number) {
        imrNumber = siteReference?.imr_number;
        footerLastUpdated = siteReference?.updated_at;
    }

    if (siteReference?.patient_declaration?.[0]?.imr_number) {
        imrNumber = siteReference?.patient_declaration?.[0]?.imr_number;
        footerLastUpdated = siteReference?.patient_declaration?.[0]?.updated_at;
    }

    const [error, setError] = useState(false);
    const [hasConfirmed, setHasConfirmed] = useState<boolean>(false);

    const container = (
        <div className={style.container}>
            {declaration && (
                <div className={style.info}>
                    {!jnjFullBranded && <Typography variant="h2">{declarationTitle}</Typography>}
                    <JWMHtml data-test-id="PatientDeclaration.disclaimer">{declaration}</JWMHtml>
                </div>
            )}

            <div className={style.confirm}>
                <Typography variant={"h5"}>{siteReference?.patient_declaration?.[0]?.form_title}</Typography>
                <div className={style.confirm__checkbox} data-test-id="PatientDeclaration.confirm">
                    <CheckBox
                        name="confirmDeclaration"
                        value="confirm"
                        onChange={(e) => setHasConfirmed(e.target.checked)}
                    >
                        <Typography
                            variant={jnjFullBranded ? null : "h6"}
                            component="span"
                            color="dark"
                            weight={jnjFullBranded ? "400" : "600"}
                        >
                            {siteReference?.patient_declaration?.[0]?.form_text}
                        </Typography>
                        {error && (
                            <Typography color="error" data-test-id="PatientDeclaration.error" italic size="s">
                                {t("Confirm patient declaration", { ns: "errors" })}
                                <ToolTracking
                                    eventType={EventTypes.TOOL_ERROR}
                                    name="declaration form"
                                    stepname={declarationTitle}
                                    stepnumber="0"
                                    type="form"
                                    fieldname="confirmDeclaration"
                                    errormessage="Confirm patient declaration"
                                    errortype="user"
                                />
                            </Typography>
                        )}
                    </CheckBox>
                </div>

                <Button
                    onClick={() => {
                        if (hasConfirmed) {
                            sessionStorage.setItem(`patientDeclaration`, JSON.stringify([...store, declarationUid]));

                            analyticstracker().trackEvent({
                                event: EventTypes.TOOL_LASTSTEP,
                                info: {
                                    name: "declaration form",
                                    stepname: declarationTitle,
                                    stepnumber: "1",
                                    type: "form",
                                    fieldname: "confirmDeclaration",
                                },
                            });

                            window.location.reload();
                        } else {
                            setError(true);
                        }
                    }}
                    data-test-id="PatientDeclaration.submit"
                >
                    {siteReference?.patient_declaration?.[0]?.form_button_label}
                </Button>
            </div>
            {legalDisclaimer && (
                <div className={style.legal}>
                    <JWMHtml color="light" data-test-id="PatientDeclaration.legalDisclaimer">
                        {legalDisclaimer}
                    </JWMHtml>
                </div>
            )}
            {sideEffects && (
                <div className={style.legal}>
                    <JWMHtml color="light" data-test-id="PatientDeclaration.sideEffects">
                        {sideEffects}
                    </JWMHtml>
                </div>
            )}
        </div>
    );

    if (access_level === AccessLevel.Private && declaration && !userInfo.isAuthenticated()) {
        return userInfo.isAuthPending() || userInfo.isAuthInProgress() ? (
            <div style={{ padding: "40vh" }}>
                <Spinner size="xl" />
            </div>
        ) : (
            <>
                <JWMLoginDisclaimer />
                <Header
                    enableSearch={false}
                    logo={<PlatformLogo position="header" custom={configuration?.logo_variant} />}
                />
                <div className={style.restrictedContainer}>
                    <RestrictedPageContent />
                </div>
                <NavigationImpression name="footer_navigation">
                    <Footer
                        disclaimer={footer?.disclaimer}
                        imr_number={imrNumber}
                        legal_entity_name={footer?.legal_entity_name}
                        legal_links={footer?.legal_links}
                        logo={footer?.logo}
                        social_media={footer?.social_media}
                        updatedAt={footerLastUpdated}
                    />
                </NavigationImpression>
            </>
        );
    } else if (accepted || !declaration) {
        return <>{children}</>;
    } else
        return (
            <>
                <ToolTracking
                    eventType={EventTypes.TOOL_STEP}
                    name="declaration form"
                    stepname={declarationTitle}
                    stepnumber="0"
                    type="form"
                    fieldname="confirmDeclaration"
                />
                <Header
                    enableSearch={false}
                    logo={<PlatformLogo position="header" custom={configuration?.logo_variant} />}
                />
                {jnjFullBranded && <JWMHeaderHero title={declarationTitle}></JWMHeaderHero>}
                {jnjFullBranded ? <Typography variant="body-02">{container}</Typography> : container}
                {jnjFullBranded && <div className={style.spacing}></div>}
                <NavigationImpression name="footer_navigation">
                    <Footer
                        disclaimer={footer?.disclaimer}
                        imr_number={imrNumber}
                        legal_entity_name={footer?.legal_entity_name}
                        legal_links={footer?.legal_links}
                        logo={footer?.logo}
                        social_media={footer?.social_media}
                        updatedAt={footerLastUpdated}
                    />
                </NavigationImpression>
            </>
        );
};

export default PatientDeclaration;
